/**
 * NotificationBell — the TopBar notifications menu.
 *
 * Deliberately self-contained (NOT wired through AppDataContext): it polls the
 * notifications API directly so the unread badge stays fresh without dragging the
 * whole in-memory store through a re-render. Mounts once in the TopBar.
 *
 * - Polls `unreadCount()` on mount + every 60s (interval cleared on unmount) and
 *   refreshes after any mark-read action.
 * - Click the bell → dropdown panel (absolute; outside-click + Escape close, the
 *   RowMenu pattern). On open it fetches `list({ limit: 20 })`.
 * - Row click → markRead(id) → onNavigate(link) → close + refresh the count.
 * - All fetch errors are swallowed (the badge just doesn't update).
 */
import { useEffect, useRef, useState } from 'react';
import type { AppNotification, NotificationLink } from '@heros/shared';
import { apiClient } from '../lib/apiClient';
import { Ico, relTime } from './ui';

export function NotificationBell({ onNavigate }: { onNavigate: (link?: NotificationLink) => void }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const refreshCount = () => {
    apiClient.notifications.unreadCount()
      .then((r) => setCount(r.count))
      .catch(() => { /* silent — badge simply doesn't update */ });
  };

  // Poll unread count on mount + every 60s.
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 60000);
    return () => clearInterval(id);
  }, []);

  // Outside-click + Escape close (copied from RowMenu).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const loadList = () => {
    setLoading(true);
    apiClient.notifications.list({ limit: 20 })
      .then((rows) => setItems(rows))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next) loadList();
      return next;
    });
  };

  const onRow = (n: AppNotification) => {
    if (!n.read) {
      apiClient.notifications.markRead(n.id).then(refreshCount).catch(() => { /* silent */ });
    }
    onNavigate(n.link);
    setOpen(false);
  };

  const markAll = () => {
    apiClient.notifications.markAllRead()
      .then(() => { setItems((xs) => xs.map((x) => ({ ...x, read: true }))); refreshCount(); })
      .catch(() => { /* silent */ });
  };

  const badge = count > 99 ? '99+' : String(count);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn ghost icon" title="Notifications" aria-haspopup="menu" aria-expanded={open} onClick={toggleOpen}>
        <Ico.bell />
      </button>
      {count > 0 && (
        <span
          aria-label={`${count} unread`}
          style={{
            position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8, background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, pointerEvents: 'none',
          }}
        >
          {badge}
        </span>
      )}
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, width: 340, maxWidth: '90vw',
            background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10,
            boxShadow: 'var(--shadow-md)', overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <strong style={{ fontSize: 12.5 }}>Notifications</strong>
            <button className="btn ghost sm" style={{ fontSize: 11.5 }} onClick={markAll}>Mark all read</button>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--text-3)' }}>No notifications yet.</div>
            ) : items.map((n) => (
              <button
                key={n.id}
                role="menuitem"
                onClick={() => onRow(n)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: '1px solid var(--border)',
                  background: n.read ? 'transparent' : 'var(--accent-soft)', padding: '10px 12px', cursor: 'pointer', font: 'inherit',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-alt)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = n.read ? 'transparent' : 'var(--accent-soft)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: n.read ? 500 : 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-3)', flexShrink: 0 }}>{relTime(n.createdAt)}</span>
                </div>
                {n.body && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
